import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import apiClient from 'utils/apiClient';

/* ==========================================================================
   CONSTANTS: MODULES & SIDEBAR MENUS
   ========================================================================== */

export const SYSTEM_MODULES = [
  { id: 'dashboard', label: 'Dashboard', actions: ['view'] },
  { id: 'analytics', label: 'Customer Analytics', actions: ['view'] },
  { id: 'mainprice', label: 'Main Price', actions: ['view', 'edit'] },
  { id: 'products', label: 'Products', actions: ['view', 'create', 'edit', 'delete'] },
  { id: 'reviews', label: 'Reviews', actions: ['view', 'edit', 'delete'] },
  { id: 'custom-product', label: 'Custom Product', actions: ['view', 'create', 'edit', 'delete'] },
  { id: 'categories', label: 'Categories', actions: ['view', 'create', 'edit', 'delete'] },
  { id: 'tags', label: 'Tags', actions: ['view', 'create', 'edit', 'delete'] },
  { id: 'sales', label: 'Sales', actions: ['view', 'create', 'edit', 'delete'] },
  { id: 'coupons', label: 'Coupons', actions: ['view', 'create', 'edit', 'delete'] },
  { id: 'newsletter', label: 'Newsletter', actions: ['view', 'delete'] },
  { id: 'blogs', label: 'Blogs', actions: ['view', 'create', 'edit', 'delete'] },
  { id: 'media', label: 'Media Upload', actions: ['view', 'create', 'delete'] },
  { id: 'orders', label: 'Orders & Payments', actions: ['view', 'edit'] },
  { id: 'users', label: 'Users', actions: ['view', 'create', 'edit', 'delete'] },
  { id: 'roles', label: 'Roles & Permissions', actions: ['view', 'create', 'edit', 'delete'] }
];

export const SIDEBAR_MENU_OPTIONS = [
  { id: 'dashboard', title: 'Dashboard', group: 'Navigation' },
  { id: 'customer-analytics', title: 'Customer Analytics', group: 'Navigation' },
  { id: 'mainprice', title: 'Main Price', group: 'Product Details' },
  { id: 'productlist', title: 'Products', group: 'Product Details' },
  { id: 'reviews', title: 'Reviews', group: 'Product Details' },
  { id: 'custom-product', title: 'Custom Product', group: 'Product Details' },
  { id: 'util-color', title: 'Categories', group: 'Product Details' },
  { id: 'tags', title: 'Tags', group: 'Product Details' },
  { id: 'salelist', title: 'Sales', group: 'Product Details' },
  { id: 'couponlist', title: 'Coupons', group: 'Product Details' },
  { id: 'newsletter', title: 'Newsletter', group: 'Product Details' },
  { id: 'blogs', title: 'Blogs', group: 'Product Details' },
  { id: 'Media', title: 'Media Upload', group: 'Product Details' },
  { id: 'payments-orders', title: 'Orders', group: 'Payments' },
  { id: 'payments-users-orders', title: 'Users', group: 'Payments' },
  { id: 'roles', title: 'Roles & Permissions', group: 'Payments' }
];

export const DEFAULT_PRESET_ROLES = [
  {
    _id: 'preset-superadmin',
    name: 'Super Admin',
    key: 'superadmin',
    description: 'Complete unrestricted access to all sidebar menus and full create/edit/delete actions.',
    isSystem: true,
    accessibleMenus: SIDEBAR_MENU_OPTIONS.map((m) => m.id),
    permissions: SYSTEM_MODULES.reduce((acc, m) => {
      acc[m.id] = [...m.actions];
      return acc;
    }, {})
  },
  {
    _id: 'preset-admin',
    name: 'Admin',
    key: 'admin',
    description: 'Full administrative access across operations, products, and management.',
    isSystem: true,
    accessibleMenus: SIDEBAR_MENU_OPTIONS.map((m) => m.id),
    permissions: SYSTEM_MODULES.reduce((acc, m) => {
      acc[m.id] = [...m.actions];
      return acc;
    }, {})
  },
  {
    _id: 'preset-manager',
    name: 'Store Manager',
    key: 'manager',
    description: 'Can manage catalog products, process orders, handle customer reviews, and launch sales.',
    isSystem: false,
    accessibleMenus: [
      'dashboard',
      'customer-analytics',
      'productlist',
      'reviews',
      'custom-product',
      'salelist',
      'couponlist',
      'payments-orders',
      'payments-users-orders'
    ],
    permissions: {
      dashboard: ['view'],
      analytics: ['view'],
      products: ['view', 'create', 'edit'],
      reviews: ['view', 'edit'],
      'custom-product': ['view', 'create', 'edit'],
      sales: ['view', 'create', 'edit'],
      coupons: ['view', 'create', 'edit'],
      orders: ['view', 'edit'],
      users: ['view']
    }
  },
  {
    _id: 'preset-editor',
    name: 'Content Editor',
    key: 'editor',
    description: 'Manages products, categories, blogs, and media assets without access to users or roles.',
    isSystem: false,
    accessibleMenus: [
      'dashboard',
      'productlist',
      'reviews',
      'custom-product',
      'util-color',
      'tags',
      'blogs',
      'Media'
    ],
    permissions: {
      dashboard: ['view'],
      products: ['view', 'create', 'edit'],
      reviews: ['view', 'edit'],
      'custom-product': ['view', 'create', 'edit'],
      categories: ['view', 'create', 'edit'],
      tags: ['view', 'create', 'edit'],
      blogs: ['view', 'create', 'edit', 'delete'],
      media: ['view', 'create', 'delete']
    }
  },
  {
    _id: 'preset-viewer',
    name: 'Support & Viewer',
    key: 'viewer',
    description: 'Read-only viewing of dashboard, customer orders, reviews, and product inventory.',
    isSystem: false,
    accessibleMenus: ['dashboard', 'customer-analytics', 'productlist', 'reviews', 'payments-orders'],
    permissions: {
      dashboard: ['view'],
      analytics: ['view'],
      products: ['view'],
      reviews: ['view'],
      orders: ['view']
    }
  }
];

/* ==========================================================================
   AUTH CONTEXT & PROVIDER
   ========================================================================== */

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('user') : null;
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [roles, setRoles] = useState(() => {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('app_roles') : null;
      return raw ? JSON.parse(raw) : DEFAULT_PRESET_ROLES;
    } catch {
      return DEFAULT_PRESET_ROLES;
    }
  });

  const [rolesLoading, setRolesLoading] = useState(false);
  // True while the initial profile fetch is in-flight.
  // Keeps hasMenuAccess permissive so sidebar items don't vanish on first render.
  const [authLoading, setAuthLoading] = useState(true);

  // Sync logged-in user profile
  useEffect(() => {
    let isMounted = true;
    const fetchProfile = async () => {
      try {
        const res = await apiClient.get('/api/admin/profile');
        if (res?.data?.admin && isMounted) {
          setUser(res.data.admin);
          try {
            localStorage.setItem('user', JSON.stringify(res.data.admin));
          } catch {}
        }
      } catch {
        // Silently ignored — user stays as whatever was in localStorage
      } finally {
        if (isMounted) setAuthLoading(false);
      }
    };
    fetchProfile();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch roles from backend
  const refreshRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const res = await apiClient.get('/api/admin/roles');
      if (res?.data?.roles && Array.isArray(res.data.roles) && res.data.roles.length > 0) {
        setRoles(res.data.roles);
        try {
          localStorage.setItem('app_roles', JSON.stringify(res.data.roles));
        } catch {}
      }
    } catch (err) {
      console.warn('Unable to load roles from backend, using local/cached roles:', err.message);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshRoles();
  }, [refreshRoles]);

  // Current active role definition
  const currentRole = useMemo(() => {
    if (!user?.role) return null;
    // user.role can be a plain string (role key/name/_id) OR a populated object from the backend
    const roleRef = user.role;
    const roleIdStr = typeof roleRef === 'object' ? (roleRef?._id || roleRef?.id || '') : String(roleRef);
    const rawRole = (typeof roleRef === 'object' ? (roleRef?.key || roleRef?.name || roleIdStr) : roleRef)
      .trim()
      .toLowerCase();
    return (
      roles.find(
        (r) =>
          r.key?.toLowerCase() === rawRole ||
          r.name?.toLowerCase() === rawRole ||
          r._id === roleIdStr ||
          r._id === roleRef
      ) || null
    );
  }, [roles, user?.role]);

  // Check action access (view, create, edit, delete)
  const canAccess = useCallback(
    (moduleName, action = 'view') => {
      // While the initial profile fetch is in-flight, deny all actions
      // so buttons don't flash visible before we know who the user is.
      if (authLoading) return false;
      if (!user) return false;

      // Resolve the role key — handle both string and populated-object cases
      const roleRef = user.role;
      const normalizedRole = (
        typeof roleRef === 'object'
          ? (roleRef?.key || roleRef?.name || '')
          : String(roleRef || '')
      ).trim().toLowerCase();

      // Superadmin and Admin always have full access
      if (normalizedRole === 'superadmin' || normalizedRole === 'admin' || normalizedRole === 'super_admin') {
        return true;
      }

      if (currentRole) {
        const moduleActions = currentRole.permissions?.[moduleName];
        if (Array.isArray(moduleActions)) {
          return moduleActions.includes(action);
        }
      }

      return false;
    },
    [authLoading, currentRole, user]
  );

  // Check sidebar menu item access
  const hasMenuAccess = useCallback(
    (menuId) => {
      // While the initial auth check is in-flight, allow all items to render
      // so the sidebar isn't blank during load.
      if (authLoading) return true;
      if (!user) return false;

      // Resolve the role key — handle both string and populated-object cases
      const roleRef = user.role;
      const normalizedRole = (
        typeof roleRef === 'object'
          ? (roleRef?.key || roleRef?.name || '')
          : String(roleRef || '')
      ).trim().toLowerCase();

      // Superadmin and Admin can see all menus
      if (normalizedRole === 'superadmin' || normalizedRole === 'admin' || normalizedRole === 'super_admin') {
        return true;
      }

      if (currentRole && Array.isArray(currentRole.accessibleMenus)) {
        return currentRole.accessibleMenus.includes(menuId);
      }

      // Default fallback: allow basic navigation
      return ['dashboard', 'customer-analytics'].includes(menuId);
    },
    [authLoading, currentRole, user]
  );

  const updateUser = useCallback((updatedUserData) => {
    setUser(updatedUserData);
    try {
      localStorage.setItem('user', JSON.stringify(updatedUserData));
    } catch {}
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/api/admin/logout');
    } catch {}
    try {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    } catch {}
    setUser(null);
    window.location.href = '/login';
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        authLoading,
        roles,
        rolesLoading,
        currentRole,
        refreshRoles,
        canAccess,
        hasMenuAccess,
        updateUser,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

/* ==========================================================================
   PERMISSION GUARD COMPONENT
   ========================================================================== */

export function PermissionGuard({ module: moduleName, action = 'view', fallback = null, children }) {
  const { canAccess } = useAuth();
  if (!canAccess(moduleName, action)) {
    return fallback;
  }
  return <>{children}</>;
}
